import { useMemo, useState } from "react";
import type { MenuProduct } from "../api";
import type { ChosenModifier } from "../state/cart";
import { measure, money, Sheet } from "../ui";

/**
 * Карточка блюда: выбор добавок и количества.
 *
 * Обязательная группа не даёт добавить блюдо, пока в ней не выбрано —
 * проверка стоит и на сервере, но узнать об этом на кнопке «добавить» лучше,
 * чем при оформлении, когда гость уже собрал корзину.
 */
export function ProductSheet({
  product,
  point,
  onClose,
  onAdd
}: {
  product: MenuProduct;
  point: { id: string; name: string };
  onClose: () => void;
  onAdd: (modifiers: ChosenModifier[], qty: number) => void;
}) {
  const [chosen, setChosen] = useState<Record<string, string[]>>({});
  const [qty, setQty] = useState(1);

  const modifiers = useMemo<ChosenModifier[]>(() => {
    const result: ChosenModifier[] = [];

    for (const group of product.modifierGroups) {
      for (const optionId of chosen[group.id] ?? []) {
        const option = group.options.find(candidate => candidate.id === optionId);
        if (!option) continue;

        result.push({
          groupId: group.id,
          optionId: option.id,
          title: option.title,
          price: option.price,
          qty: 1
        });
      }
    }

    return result;
  }, [chosen, product.modifierGroups]);

  const missing = product.modifierGroups.filter(
    group => group.required && (chosen[group.id]?.length ?? 0) < Math.max(group.minSelect, 1)
  );

  const unitPrice = product.price + modifiers.reduce((sum, item) => sum + item.price, 0);

  function toggle(groupId: string, optionId: string, maxSelect: number): void {
    setChosen(current => {
      const picked = current[groupId] ?? [];

      if (picked.includes(optionId)) {
        return { ...current, [groupId]: picked.filter(id => id !== optionId) };
      }

      // Группа «выбери одно» переключается, а не копит: гость жмёт второй
      // размер, имея в виду замену, а не добавку.
      if (maxSelect <= 1) return { ...current, [groupId]: [optionId] };
      if (picked.length >= maxSelect) return current;

      return { ...current, [groupId]: [...picked, optionId] };
    });
  }

  return (
    <Sheet
      title={product.title}
      note={
        <>
          {point.name}
          {measure(product.weightG, product.volumeMl)
            ? ` · ${measure(product.weightG, product.volumeMl)}`
            : ""}
        </>
      }
      onClose={onClose}
      footer={
        <>
          <div className="btn-row" style={{ marginBottom: 12 }}>
            <div className="stepper" style={{ flex: "none" }}>
              <button type="button" onClick={() => setQty(value => Math.max(1, value - 1))}>
                −
              </button>
              <span>{qty}</span>
              <button type="button" onClick={() => setQty(value => Math.min(50, value + 1))}>
                +
              </button>
            </div>
          </div>

          <button
            type="button"
            className="btn"
            disabled={missing.length > 0}
            onClick={() => onAdd(modifiers, qty)}
          >
            <span>
              {missing.length > 0 ? `Выберите: ${missing[0]!.title}` : "В корзину"}
            </span>
            {missing.length > 0 ? null : <span>{money(unitPrice * qty)}</span>}
          </button>
        </>
      }
    >
      {product.description ? (
        <p className="muted" style={{ marginBottom: 18 }}>
          {product.description}
        </p>
      ) : null}

      {/*
        Состав набора (п. 6 ТЗ).

        У комбо своя цена, и без состава она читается как цена одного блюда,
        взятая с потолка: гость должен видеть, за что платит.
      */}
      {product.comboItems.length > 0 ? (
        <div className="group" style={{ marginBottom: 18 }}>
          <div className="group-head">
            <strong>В наборе</strong>
          </div>

          {product.comboItems.map(item => (
            <p key={item.title} className="muted" style={{ margin: "6px 0" }}>
              {item.title}
              {item.qty > 1 ? ` × ${item.qty}` : ""}
            </p>
          ))}
        </div>
      ) : null}

      {product.modifierGroups.map(group => (
        <div key={group.id} className="group">
          <div className="group-head">
            <strong>{group.title}</strong>
            <span className="muted small">
              {group.required ? "обязательно" : `до ${group.maxSelect}`}
            </span>
          </div>

          {group.options.map(option => {
            const active = (chosen[group.id] ?? []).includes(option.id);

            return (
              <button
                key={option.id}
                type="button"
                className="option"
                aria-pressed={active}
                onClick={() => toggle(group.id, option.id, group.maxSelect)}
              >
                <span className="option-title">
                  {option.exclusion ? `без ${option.title.toLowerCase()}` : option.title}
                </span>
                {option.price > 0 ? (
                  <span className="option-price">+{money(option.price)}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      ))}
    </Sheet>
  );
}
